/**
 * Unified Recruitment Type System
 *
 * Bridges frontend camelCase types with database snake_case types
 * Provides bidirectional conversion functions for seamless data flow
 *
 * Architecture:
 * - Frontend uses camelCase (JavaScript/React convention)
 * - Database uses snake_case (PostgreSQL convention)
 * - This layer handles all conversions transparently
 */

import type {
  Recruit,
  RecruitInsert,
  RecruitUpdate,
  RecruitWithRelations,
  ContractTemplate,
  ContractTemplateInsert,
  ContractTemplateUpdate,
  ContractInstance,
  ContractInstanceInsert,
  ContractInstanceUpdate,
  RecruitmentInvitation,
  RecruitmentInvitationInsert,
  RecruitmentInvitationUpdate,
  RecruitStatusHistory,
  RecruitStatus,
  RecruitPosition,
  ContractStatus,
  InvitationStatus
} from './database-recruitment';

import type { RecruitmentFormValues } from '@/components/recruitment/validations';

// ============================================================================
// Re-export Database Enums (used by both layers)
// ============================================================================

export type {
  RecruitStatus,
  RecruitPosition,
  ContractStatus,
  InvitationStatus
};

// ============================================================================
// Frontend Types (camelCase for component usage)
// ============================================================================

/**
 * Recruit data as used by frontend components
 */
export interface RecruitmentCandidate {
  id: string;
  organizationId: string;

  // Personal Information
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredFirstName?: string;
  email: string;
  phone: string;
  dateOfBirth?: string;

  // Mailing Address
  mailingStreetAddress?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;

  // Professional Credentials
  nmlsNumber: string;
  dreNumber?: string;
  yearsOfExperience: number | null;
  stateLicenses: string[];
  position: RecruitPosition;

  // Compensation
  bankedCompensation?: number;
  brokerCommissionSplit?: number;
  bankedOverrideBps?: number;
  overrideRecipientId?: string;

  // Mentorship
  mentorId?: string;
  brokerMentorCommissionSplit?: number;
  mentorshipActive: boolean;

  // Compensation Cap
  maximumCompensation?: number;

  // Status
  status: RecruitStatus;
  statusComment?: string;
  statusChangedAt?: string;
  statusChangedBy?: string;

  // Onboarding Tasks
  step1Completed?: boolean;
  step2Completed?: boolean;
  step3Completed?: boolean;
  step4Completed?: boolean;
  step5Completed?: boolean;
  step6Completed?: boolean;
  step7Completed?: boolean;
  step8Completed?: boolean;
  step9Completed?: boolean;
  gmailAccount?: string;
  gmailTempPassword?: string;
  optimalUsername?: string;
  migratedToUserId?: string;
  migratedAt?: string;
  encompassTempPassword?: string;
  onboardingAccessMode?: 'provisioned' | 'existing'; // How system access was established (undefined = legacy)
  encompassAccessMode?: 'provisioned' | 'existing'; // Encompass-specific provenance (undefined = legacy)
  nmlsAuthorizationDate?: string; // Task 5: NMLS authorization to conduct business
  xactusRequestDate?: string;
  xactusRequestStatus?: string;

  // Identification Documents
  idPrimaryType?: string;
  idPrimaryFrontPath?: string;
  idPrimaryBackPath?: string;
  idSecondaryType?: string;
  idSecondaryFrontPath?: string;
  idSecondaryBackPath?: string;
  idValidatedAt?: string;
  idValidatedBy?: string;
  otherDocuments?: any[];

  // Per-ID Approval/Rejection Status
  idPrimaryStatus?: 'pending' | 'approved' | 'rejected';
  idPrimaryApprovedAt?: string;
  idPrimaryApprovedBy?: string;
  idPrimaryRejectedAt?: string;
  idPrimaryRejectedBy?: string;
  idPrimaryRejectionReason?: string;

  idSecondaryStatus?: 'pending' | 'approved' | 'rejected';
  idSecondaryApprovedAt?: string;
  idSecondaryApprovedBy?: string;
  idSecondaryRejectedAt?: string;
  idSecondaryRejectedBy?: string;
  idSecondaryRejectionReason?: string;

  // State W4 (separate from federal tax forms)
  stateW4Form?: any;
  stateW4Required?: boolean;

  // Compensation Plan
  compensationPlanId?: string;

  // Metadata
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  updatedBy?: string;

  // Recruiter Assignment
  recruiterId?: string;
  recruiterName?: string;
  recruiterEmail?: string;
}

/**
 * Recruit with related data (mentor, contracts, etc.)
 */
export interface RecruitmentCandidateWithRelations extends RecruitmentCandidate {
  recruiter?: {
    fullName: string;
    email: string;
  };
  mentor?: {
    fullName: string;
    email: string;
  };
  overrideRecipient?: {
    fullName: string;
    email: string;
  };
  contractInstances?: ContractInstanceData[];
  recruitmentInvitations?: InvitationData[];
  statusHistory?: StatusHistoryEntry[];
}

/**
 * Contract instance data for frontend
 */
export interface ContractInstanceData {
  id: string;
  organizationId: string;
  recruitId: string;
  templateId?: string;
  templateVersion: number;
  variableData: Record<string, any>;
  sections: any;
  checkboxStates?: Record<string, boolean>;
  status: ContractStatus;
  sentAt?: string;
  viewedAt?: string;
  acceptedAt?: string;
  signatureDate?: string;
  signatureDataUrl?: string;

  // Loan Officer (Recruit) Signature Fields
  loanOfficerSignatureDate?: string;
  loanOfficerSignatureDataUrl?: string;
  loanOfficerSignatureMethod?: 'drawn' | 'typed' | 'uploaded';
  loanOfficerSignatureFont?: string; // Font name for typed signatures
  loanOfficerSignatureIpAddress?: string;
  loanOfficerSignatureUserAgent?: string;
  loanOfficerSignatureAuditLog?: Record<string, any>;
  loanOfficerConsentElectronic?: boolean;
  loanOfficerIntentConfirmed?: boolean;
  loanOfficerTermsAccepted?: boolean;
  loanOfficerAccuracyCertified?: boolean;
  hasLoSignature?: boolean;

  // Recruiter (Company) Signature Fields
  recruiterSignatureDate?: string;
  recruiterSignatureDataUrl?: string;
  recruiterSignatureMethod?: 'drawn' | 'typed' | 'uploaded';
  recruiterSignatureFont?: string; // Font name for typed signatures
  recruiterSignatureIpAddress?: string;
  recruiterSignatureUserAgent?: string;
  recruiterSignatureAuditLog?: Record<string, any>;
  recruiterConsentElectronic?: boolean;
  recruiterIntentConfirmed?: boolean;
  recruiterTermsAccepted?: boolean;
  recruiterAccuracyCertified?: boolean;
  hasRecruiterSignature?: boolean;

  // Final PDF
  finalPdfUrl?: string;

  // Version Control Fields
  versionNumber?: number;
  isActive?: boolean;
  parentInstanceId?: string;
  voidedAt?: string;
  voidedBy?: string;
  voidReason?: string;

  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  updatedBy?: string;
}

/**
 * Invitation data for frontend
 */
export interface InvitationData {
  id: string;
  organizationId: string;
  recruitId: string;
  token: string;
  expiresAt: string;
  status: InvitationStatus;
  message?: string;
  sentBy?: string;
  sentAt: string;
  viewedAt?: string;
  ccEmails?: string[];
}

/**
 * Status history entry for frontend
 */
export interface StatusHistoryEntry {
  id: string;
  organizationId: string;
  recruitId: string;
  oldStatus?: RecruitStatus;
  newStatus: RecruitStatus;
  comment?: string;
  changedBy?: string;
  changedAt: string;
}

// ============================================================================
// Conversion Functions: Database → Frontend (snake_case → camelCase)
// ============================================================================

/**
 * Convert database Recruit to frontend RecruitmentCandidate
 */
export function recruitFromDatabase(dbRecruit: Recruit): RecruitmentCandidate {
  return {
    id: dbRecruit.id,
    organizationId: dbRecruit.organization_id,

    // Personal
    firstName: dbRecruit.first_name,
    lastName: dbRecruit.last_name,
    middleName: dbRecruit.middle_name ?? undefined,
    preferredFirstName: dbRecruit.preferred_first_name ?? undefined,
    email: dbRecruit.email,
    phone: dbRecruit.phone,
    dateOfBirth: dbRecruit.date_of_birth ?? undefined,

    // Address
    mailingStreetAddress: dbRecruit.mailing_street_address ?? undefined,
    mailingCity: dbRecruit.mailing_city ?? undefined,
    mailingState: dbRecruit.mailing_state ?? undefined,
    mailingZip: dbRecruit.mailing_zip ?? undefined,

    // Professional
    nmlsNumber: dbRecruit.nmls_number,
    dreNumber: dbRecruit.dre_number ?? undefined,
    yearsOfExperience: dbRecruit.years_of_experience,
    stateLicenses: dbRecruit.state_licenses ?? [],
    position: dbRecruit.position,

    // Compensation
    bankedCompensation: dbRecruit.banked_compensation ?? undefined,
    brokerCommissionSplit: dbRecruit.broker_commission_split ?? undefined,
    bankedOverrideBps: dbRecruit.banked_override_bps ?? undefined,
    overrideRecipientId: dbRecruit.override_recipient_id ?? undefined,

    // Mentorship
    mentorId: dbRecruit.mentor_id ?? undefined,
    brokerMentorCommissionSplit: dbRecruit.broker_mentor_commission_split ?? undefined,
    mentorshipActive: dbRecruit.mentorship_active,

    // Compensation Cap
    maximumCompensation: dbRecruit.maximum_compensation ?? undefined,
    compensationPlanId: dbRecruit.compensation_plan_id ?? undefined,

    // Status
    status: dbRecruit.status,
    statusComment: dbRecruit.status_comment ?? undefined,
    statusChangedAt: dbRecruit.status_changed_at ?? undefined,
    statusChangedBy: dbRecruit.status_changed_by ?? undefined,

    // Onboarding Tasks
    step1Completed: dbRecruit.step_1_completed ?? undefined,
    step2Completed: dbRecruit.step_2_completed ?? undefined,
    step3Completed: dbRecruit.step_3_completed ?? undefined,
    step4Completed: dbRecruit.step_4_completed ?? undefined,
    step5Completed: dbRecruit.step_5_completed ?? undefined,
    step6Completed: dbRecruit.step_6_completed ?? undefined,
    step7Completed: dbRecruit.step_7_completed ?? undefined,
    step8Completed: dbRecruit.step_8_completed ?? undefined,
    step9Completed: dbRecruit.step_9_completed ?? undefined,
    gmailAccount: dbRecruit.gmail_account ?? undefined,
    gmailTempPassword: dbRecruit.gmail_temp_password ?? undefined,
    optimalUsername: dbRecruit.optimal_username ?? undefined,
    migratedToUserId: dbRecruit.migrated_to_user_id ?? undefined,
    migratedAt: dbRecruit.migrated_at ?? undefined,
    encompassTempPassword: dbRecruit.encompass_temp_password ?? undefined,
    onboardingAccessMode: dbRecruit.onboarding_access_mode ?? undefined,
    encompassAccessMode: dbRecruit.encompass_access_mode ?? undefined,
    nmlsAuthorizationDate: dbRecruit.nmls_authorization_date ?? undefined,
    xactusRequestDate: dbRecruit.xactus_request_date ?? undefined,
    xactusRequestStatus: dbRecruit.xactus_request_status ?? undefined,

    // Identification Documents
    idPrimaryType: dbRecruit.id_primary_type ?? undefined,
    idPrimaryFrontPath: dbRecruit.id_primary_front_path ?? undefined,
    idPrimaryBackPath: dbRecruit.id_primary_back_path ?? undefined,
    idSecondaryType: dbRecruit.id_secondary_type ?? undefined,
    idSecondaryFrontPath: dbRecruit.id_secondary_front_path ?? undefined,
    idSecondaryBackPath: dbRecruit.id_secondary_back_path ?? undefined,
    idValidatedAt: dbRecruit.id_validated_at ?? undefined,
    idValidatedBy: dbRecruit.id_validated_by ?? undefined,
    otherDocuments: dbRecruit.other_documents ?? undefined,

    // Per-ID Approval/Rejection Status
    idPrimaryStatus: (dbRecruit as any).id_primary_status ?? undefined,
    idPrimaryApprovedAt: (dbRecruit as any).id_primary_approved_at ?? undefined,
    idPrimaryApprovedBy: (dbRecruit as any).id_primary_approved_by ?? undefined,
    idPrimaryRejectedAt: (dbRecruit as any).id_primary_rejected_at ?? undefined,
    idPrimaryRejectedBy: (dbRecruit as any).id_primary_rejected_by ?? undefined,
    idPrimaryRejectionReason: (dbRecruit as any).id_primary_rejection_reason ?? undefined,

    idSecondaryStatus: (dbRecruit as any).id_secondary_status ?? undefined,
    idSecondaryApprovedAt: (dbRecruit as any).id_secondary_approved_at ?? undefined,
    idSecondaryApprovedBy: (dbRecruit as any).id_secondary_approved_by ?? undefined,
    idSecondaryRejectedAt: (dbRecruit as any).id_secondary_rejected_at ?? undefined,
    idSecondaryRejectedBy: (dbRecruit as any).id_secondary_rejected_by ?? undefined,
    idSecondaryRejectionReason: (dbRecruit as any).id_secondary_rejection_reason ?? undefined,

    // State W4
    stateW4Form: dbRecruit.state_w4_form ?? undefined,
    stateW4Required: dbRecruit.state_w4_required ?? undefined,

    // Metadata
    createdAt: dbRecruit.created_at,
    createdBy: dbRecruit.created_by ?? undefined,
    updatedAt: dbRecruit.updated_at,
    updatedBy: dbRecruit.updated_by ?? undefined,

    // Recruiter Assignment
    recruiterId: dbRecruit.recruiter_id ?? undefined,
    recruiterName: (dbRecruit as any).recruiter
      ? ((dbRecruit as any).recruiter.full_name ||
         `${(dbRecruit as any).recruiter.first_name} ${(dbRecruit as any).recruiter.last_name}`.trim() ||
         undefined)
      : undefined,
    recruiterEmail: (dbRecruit as any).recruiter?.email ?? undefined
  };
}

/**
 * Convert database Recruit with relations to frontend type
 */
export function recruitWithRelationsFromDatabase(
  dbRecruit: RecruitWithRelations
): RecruitmentCandidateWithRelations {
  const base = recruitFromDatabase(dbRecruit);

  return {
    ...base,
    recruiter: (dbRecruit as any).recruiter ? {
      fullName: (dbRecruit as any).recruiter.full_name,
      email: (dbRecruit as any).recruiter.email
    } : undefined,
    mentor: dbRecruit.mentor ? {
      fullName: dbRecruit.mentor.full_name,
      email: dbRecruit.mentor.email
    } : undefined,
    overrideRecipient: dbRecruit.override_recipient ? {
      fullName: dbRecruit.override_recipient.full_name,
      email: dbRecruit.override_recipient.email
    } : undefined,
    contractInstances: dbRecruit.contract_instances?.map(contractInstanceFromDatabase),
    recruitmentInvitations: dbRecruit.recruitment_invitations?.map(invitationFromDatabase),
    statusHistory: dbRecruit.recruit_status_history?.map(statusHistoryFromDatabase)
  };
}

/**
 * Convert database ContractInstance to frontend type
 */
export function contractInstanceFromDatabase(dbInstance: ContractInstance): ContractInstanceData {
  return {
    id: dbInstance.id,
    organizationId: dbInstance.organization_id,
    recruitId: dbInstance.recruit_id,
    templateId: dbInstance.template_id ?? undefined,
    templateVersion: dbInstance.template_version,
    variableData: dbInstance.variable_data,
    sections: dbInstance.sections,
    checkboxStates: dbInstance.checkbox_states ?? undefined,
    status: dbInstance.status,
    sentAt: dbInstance.sent_at ?? undefined,
    viewedAt: dbInstance.viewed_at ?? undefined,
    acceptedAt: dbInstance.accepted_at ?? undefined,
    signatureDate: dbInstance.signature_date ?? undefined,
    signatureDataUrl: dbInstance.signature_data_url ?? undefined,

    // Loan Officer (Recruit) Signature Fields
    loanOfficerSignatureDate: (dbInstance as any).loan_officer_signature_date ?? undefined,
    loanOfficerSignatureDataUrl: (dbInstance as any).loan_officer_signature_data_url ?? undefined,
    loanOfficerSignatureMethod: (dbInstance as any).loan_officer_signature_method ?? undefined,
    loanOfficerSignatureFont: (dbInstance as any).loan_officer_signature_font ?? undefined,
    loanOfficerSignatureIpAddress: (dbInstance as any).loan_officer_signature_ip ?? undefined,
    loanOfficerSignatureUserAgent: (dbInstance as any).loan_officer_signature_user_agent ?? undefined,
    loanOfficerSignatureAuditLog: (dbInstance as any).loan_officer_signature_audit_log ?? undefined,
    loanOfficerConsentElectronic: (dbInstance as any).loan_officer_consent_electronic ?? undefined,
    loanOfficerIntentConfirmed: (dbInstance as any).loan_officer_intent_confirmed ?? undefined,
    loanOfficerTermsAccepted: (dbInstance as any).loan_officer_terms_accepted ?? undefined,
    loanOfficerAccuracyCertified: (dbInstance as any).loan_officer_accuracy_certified ?? undefined,
    hasLoSignature: (dbInstance as any).has_lo_signature ?? undefined,

    // Recruiter (Company) Signature Fields
    recruiterSignatureDate: (dbInstance as any).recruiter_signature_date ?? undefined,
    recruiterSignatureDataUrl: (dbInstance as any).recruiter_signature_data_url ?? undefined,
    recruiterSignatureMethod: (dbInstance as any).recruiter_signature_method ?? undefined,
    recruiterSignatureFont: (dbInstance as any).recruiter_signature_font ?? undefined,
    recruiterSignatureIpAddress: (dbInstance as any).recruiter_signature_ip_address ?? undefined,
    recruiterSignatureUserAgent: (dbInstance as any).recruiter_signature_user_agent ?? undefined,
    recruiterSignatureAuditLog: (dbInstance as any).recruiter_signature_audit_log ?? undefined,
    recruiterConsentElectronic: (dbInstance as any).recruiter_consent_electronic ?? undefined,
    recruiterIntentConfirmed: (dbInstance as any).recruiter_intent_confirmed ?? undefined,
    recruiterTermsAccepted: (dbInstance as any).recruiter_terms_accepted ?? undefined,
    recruiterAccuracyCertified: (dbInstance as any).recruiter_accuracy_certified ?? undefined,
    hasRecruiterSignature: (dbInstance as any).has_recruiter_signature ?? undefined,

    // Final PDF
    finalPdfUrl: (dbInstance as any).final_pdf_url ?? undefined,

    // Version Control Fields
    versionNumber: (dbInstance as any).version_number ?? undefined,
    isActive: (dbInstance as any).is_active ?? undefined,
    parentInstanceId: (dbInstance as any).parent_instance_id ?? undefined,
    voidedAt: (dbInstance as any).voided_at ?? undefined,
    voidedBy: (dbInstance as any).voided_by ?? undefined,
    voidReason: (dbInstance as any).void_reason ?? undefined,

    createdAt: dbInstance.created_at,
    createdBy: dbInstance.created_by ?? undefined,
    updatedAt: dbInstance.updated_at,
    updatedBy: dbInstance.updated_by ?? undefined
  };
}

/**
 * Convert database RecruitmentInvitation to frontend type
 */
export function invitationFromDatabase(dbInvitation: RecruitmentInvitation): InvitationData {
  return {
    id: dbInvitation.id,
    organizationId: dbInvitation.organization_id,
    recruitId: dbInvitation.recruit_id,
    token: dbInvitation.token,
    expiresAt: dbInvitation.expires_at,
    status: dbInvitation.status,
    message: dbInvitation.message ?? undefined,
    sentBy: dbInvitation.sent_by ?? undefined,
    sentAt: dbInvitation.sent_at,
    viewedAt: dbInvitation.viewed_at ?? undefined,
    ccEmails: dbInvitation.cc_emails ?? undefined
  };
}

/**
 * Convert database RecruitStatusHistory to frontend type
 */
export function statusHistoryFromDatabase(dbHistory: RecruitStatusHistory): StatusHistoryEntry {
  return {
    id: dbHistory.id,
    organizationId: dbHistory.organization_id,
    recruitId: dbHistory.recruit_id,
    oldStatus: dbHistory.old_status ?? undefined,
    newStatus: dbHistory.new_status,
    comment: dbHistory.comment ?? undefined,
    changedBy: dbHistory.changed_by ?? undefined,
    changedAt: dbHistory.changed_at
  };
}

// ============================================================================
// Conversion Functions: Frontend → Database (camelCase → snake_case)
// ============================================================================

/**
 * Convert form values to database RecruitInsert
 */
export function recruitToDatabase(
  formData: RecruitmentFormValues,
  organizationId: string,
  userId?: string
): RecruitInsert {
  // Runtime validation for required fields
  if (!formData.position) {
    throw new Error('Position is required but was not provided');
  }
  if (!formData.status) {
    throw new Error('Status is required but was not provided');
  }
  if (!formData.nmlsNumber) {
    throw new Error('NMLS number is required but was not provided');
  }

  return {
    organization_id: organizationId,

    // Personal
    first_name: formData.firstName,
    last_name: formData.lastName,
    preferred_first_name: formData.preferredFirstName || null,
    email: formData.email,
    phone: formData.phone,
    date_of_birth: formData.dateOfBirth || null,

    // Address
    mailing_street_address: formData.mailingStreetAddress || null,
    mailing_city: formData.mailingCity || null,
    mailing_state: formData.mailingState || null,
    mailing_zip: formData.mailingZip || null,

    // Professional
    nmls_number: formData.nmlsNumber,
    dre_number: formData.dreNumber || null,
    years_of_experience: formData.yearsOfExperience,
    state_licenses: formData.stateLicenses,
    position: formData.position,

    // Compensation
    banked_compensation: formData.bankedCompensation ?? null,
    broker_commission_split: formData.brokerCommissionSplit ?? null,
    banked_override_bps: formData.bankedOverrideBps ?? null,
    override_recipient_id: formData.overrideRecipientId || null,

    // Mentorship
    mentor_id: formData.mentorId || null,
    broker_mentor_commission_split: formData.brokerMentorCommissionSplit ?? null,
    mentorship_active: !!formData.mentorId,

    // Compensation Cap
    maximum_compensation: formData.maximumCompensation ?? null,
    compensation_plan_id: (formData as any).compensationPlanId || null,

    // Status
    status: formData.status,
    status_comment: formData.statusComment || null,
    status_changed_at: null,
    status_changed_by: null,

    // Onboarding steps
    step_1_completed: false,
    step_2_completed: false,
    step_3_completed: false,
    step_4_completed: false,
    step_5_completed: false,
    step_6_completed: false,
    step_7_completed: false,
    step_8_completed: false,
    step_9_completed: false,

    // ID Documents (null for new recruits)
    id_primary_type: null,
    id_primary_front_path: null,
    id_primary_back_path: null,
    id_secondary_type: null,
    id_secondary_front_path: null,
    id_secondary_back_path: null,
    id_validated_at: null,
    id_validated_by: null,
    other_documents: null,

    // State W4
    state_w4_form: null,
    state_w4_required: null,

    // NMLS Authorization
    nmls_authorization_date: null,

    // Metadata (optional for insert)
    created_by: userId || null,
    updated_by: userId || null,

    // Recruiter assignment
    recruiter_id: formData.recruiterId || userId || null,

    // Authoritative invite sender — set only at send time via the invite flow
    // (trigger-enforced to the caller); a new recruit has no inviter yet.
    last_invited_by: null,
    last_invited_at: null
  };
}

/**
 * Convert frontend update data to database RecruitUpdate
 */
export function recruitUpdateToDatabase(
  updates: Partial<RecruitmentFormValues>,
  userId?: string
): RecruitUpdate {
  const dbUpdate: RecruitUpdate = {
    updated_by: userId
  };

  // Property PRESENCE, used ONLY for the optional numerics whose cleared
  // representation is `undefined` (banked compensation, override bps, mentor
  // split, maximum compensation). For those, `!== undefined` skipped the column
  // entirely, so a value the user cleared could never actually be cleared. An
  // omitted key (partial update) is still left untouched; a present-but-
  // undefined key persists as NULL. Every other field keeps its original guard —
  // `yearsOfExperience` clears as `null`, so `!== undefined` already works.
  const has = (key: string): boolean =>
    Object.prototype.hasOwnProperty.call(updates, key);

  // Personal
  if (updates.firstName !== undefined) {
dbUpdate.first_name = updates.firstName;
}
  if (updates.lastName !== undefined) {
dbUpdate.last_name = updates.lastName;
}
  if (updates.preferredFirstName !== undefined) {
dbUpdate.preferred_first_name = updates.preferredFirstName || null;
}
  // Saved recruits have a read-only email field. Form validation normalizes it,
  // but an ordinary save must not submit that normalization as an email change.
  // Explicit changes use recruit-auth-admin and its session-revocation audit.
  if (updates.phone !== undefined) {
dbUpdate.phone = updates.phone;
}
  if (updates.dateOfBirth !== undefined) {
dbUpdate.date_of_birth = updates.dateOfBirth || null;
}

  // Address
  if (updates.mailingStreetAddress !== undefined) {
dbUpdate.mailing_street_address = updates.mailingStreetAddress || null;
}
  if (updates.mailingCity !== undefined) {
dbUpdate.mailing_city = updates.mailingCity || null;
}
  if (updates.mailingState !== undefined) {
dbUpdate.mailing_state = updates.mailingState || null;
}
  if (updates.mailingZip !== undefined) {
dbUpdate.mailing_zip = updates.mailingZip || null;
}

  // Professional
  if (updates.nmlsNumber !== undefined) {
dbUpdate.nmls_number = updates.nmlsNumber;
}
  if (updates.dreNumber !== undefined) {
dbUpdate.dre_number = updates.dreNumber || null;
}
  if (updates.yearsOfExperience !== undefined) {
dbUpdate.years_of_experience = updates.yearsOfExperience ?? null;
}
  if (updates.stateLicenses !== undefined) {
dbUpdate.state_licenses = updates.stateLicenses;
}
  if (updates.position !== undefined) {
dbUpdate.position = updates.position;
}

  // Compensation
  if (has('bankedCompensation')) {
dbUpdate.banked_compensation = updates.bankedCompensation ?? null;
}
  if (updates.brokerCommissionSplit !== undefined) {
dbUpdate.broker_commission_split = updates.brokerCommissionSplit ?? null;
}
  if (has('bankedOverrideBps')) {
dbUpdate.banked_override_bps = updates.bankedOverrideBps ?? null;
}
  if (updates.overrideRecipientId !== undefined) {
dbUpdate.override_recipient_id = updates.overrideRecipientId || null;
}

  // Mentorship
  if (updates.mentorId !== undefined) {
    dbUpdate.mentor_id = updates.mentorId || null;
    dbUpdate.mentorship_active = !!updates.mentorId;
  }
  if (has('brokerMentorCommissionSplit')) {
dbUpdate.broker_mentor_commission_split = updates.brokerMentorCommissionSplit ?? null;
}

  // Compensation Cap
  if (has('maximumCompensation')) {
dbUpdate.maximum_compensation = updates.maximumCompensation ?? null;
}
  if ((updates as any).compensationPlanId !== undefined) {
dbUpdate.compensation_plan_id = (updates as any).compensationPlanId || null;
}

  // Status
  if (updates.status !== undefined) {
    dbUpdate.status = updates.status;
    dbUpdate.status_changed_at = new Date().toISOString();
    dbUpdate.status_changed_by = userId || null;
  }
  if (updates.statusComment !== undefined) {
dbUpdate.status_comment = updates.statusComment || null;
}

  // Onboarding steps (not in form schema, used via API)
  if ((updates as any).step5Completed !== undefined) {
dbUpdate.step_5_completed = (updates as any).step5Completed;
}
  if ((updates as any).step6Completed !== undefined) {
dbUpdate.step_6_completed = (updates as any).step6Completed;
}
  if ((updates as any).step7Completed !== undefined) {
dbUpdate.step_7_completed = (updates as any).step7Completed;
}
  if ((updates as any).step8Completed !== undefined) {
dbUpdate.step_8_completed = (updates as any).step8Completed;
}
  if ((updates as any).step9Completed !== undefined) {
dbUpdate.step_9_completed = (updates as any).step9Completed;
}

  // Recruiter assignment
  if (updates.recruiterId !== undefined) {
    dbUpdate.recruiter_id = updates.recruiterId || null;
  }

  return dbUpdate;
}

/**
 * Convert frontend contract instance data to database insert
 */
export function contractInstanceToDatabase(
  instanceData: Partial<ContractInstanceData>,
  recruitId: string,
  organizationId: string,
  userId?: string
): ContractInstanceInsert {
  const dbData: any = {
    organization_id: organizationId,
    recruit_id: recruitId,
    template_id: instanceData.templateId || null,
    template_version: instanceData.templateVersion || 1,
    variable_data: instanceData.variableData || {},
    sections: instanceData.sections || [],
    checkbox_states: instanceData.checkboxStates || null,
    status: instanceData.status || 'draft',
    sent_at: instanceData.sentAt || null,
    viewed_at: instanceData.viewedAt || null,
    accepted_at: instanceData.acceptedAt || null,
    signature_date: instanceData.signatureDate || null,
    signature_data_url: instanceData.signatureDataUrl || null,
    created_by: userId || null,
    updated_by: userId || null
  };

  // Add loan officer signature fields if present
  if (instanceData.loanOfficerSignatureDate) {
dbData.loan_officer_signature_date = instanceData.loanOfficerSignatureDate;
}
  if (instanceData.loanOfficerSignatureDataUrl) {
dbData.loan_officer_signature_data_url = instanceData.loanOfficerSignatureDataUrl;
}
  if (instanceData.loanOfficerSignatureMethod) {
dbData.loan_officer_signature_method = instanceData.loanOfficerSignatureMethod;
}
  if (instanceData.loanOfficerSignatureIpAddress) {
dbData.loan_officer_signature_ip = instanceData.loanOfficerSignatureIpAddress;
}
  if (instanceData.loanOfficerSignatureUserAgent) {
dbData.loan_officer_signature_user_agent = instanceData.loanOfficerSignatureUserAgent;
}
  if (instanceData.loanOfficerSignatureAuditLog) {
dbData.loan_officer_signature_audit_log = instanceData.loanOfficerSignatureAuditLog;
}
  if (instanceData.loanOfficerConsentElectronic !== undefined) {
dbData.loan_officer_consent_electronic = instanceData.loanOfficerConsentElectronic;
}
  if (instanceData.loanOfficerIntentConfirmed !== undefined) {
dbData.loan_officer_intent_confirmed = instanceData.loanOfficerIntentConfirmed;
}
  if (instanceData.loanOfficerTermsAccepted !== undefined) {
dbData.loan_officer_terms_accepted = instanceData.loanOfficerTermsAccepted;
}
  if (instanceData.loanOfficerAccuracyCertified !== undefined) {
dbData.loan_officer_accuracy_certified = instanceData.loanOfficerAccuracyCertified;
}
  if (instanceData.hasLoSignature !== undefined) {
dbData.has_lo_signature = instanceData.hasLoSignature;
}
  if (instanceData.loanOfficerSignatureFont) {
dbData.loan_officer_signature_font = instanceData.loanOfficerSignatureFont;
}

  // Add recruiter signature fields if present
  if (instanceData.recruiterSignatureDate) {
dbData.recruiter_signature_date = instanceData.recruiterSignatureDate;
}
  if (instanceData.recruiterSignatureDataUrl) {
dbData.recruiter_signature_data_url = instanceData.recruiterSignatureDataUrl;
}
  if (instanceData.recruiterSignatureMethod) {
dbData.recruiter_signature_method = instanceData.recruiterSignatureMethod;
}
  if (instanceData.recruiterSignatureIpAddress) {
dbData.recruiter_signature_ip_address = instanceData.recruiterSignatureIpAddress;
}
  if (instanceData.recruiterSignatureUserAgent) {
dbData.recruiter_signature_user_agent = instanceData.recruiterSignatureUserAgent;
}
  if (instanceData.recruiterSignatureAuditLog) {
dbData.recruiter_signature_audit_log = instanceData.recruiterSignatureAuditLog;
}
  if (instanceData.recruiterConsentElectronic !== undefined) {
dbData.recruiter_consent_electronic = instanceData.recruiterConsentElectronic;
}
  if (instanceData.recruiterIntentConfirmed !== undefined) {
dbData.recruiter_intent_confirmed = instanceData.recruiterIntentConfirmed;
}
  if (instanceData.recruiterTermsAccepted !== undefined) {
dbData.recruiter_terms_accepted = instanceData.recruiterTermsAccepted;
}
  if (instanceData.recruiterAccuracyCertified !== undefined) {
dbData.recruiter_accuracy_certified = instanceData.recruiterAccuracyCertified;
}
  if (instanceData.hasRecruiterSignature !== undefined) {
dbData.has_recruiter_signature = instanceData.hasRecruiterSignature;
}
  if (instanceData.recruiterSignatureFont) {
dbData.recruiter_signature_font = instanceData.recruiterSignatureFont;
}

  // Add final PDF URL if present
  if (instanceData.finalPdfUrl) {
dbData.final_pdf_url = instanceData.finalPdfUrl;
}

  // Add version control fields if present
  if (instanceData.versionNumber !== undefined) {
dbData.version_number = instanceData.versionNumber;
}
  if (instanceData.isActive !== undefined) {
dbData.is_active = instanceData.isActive;
}
  if (instanceData.parentInstanceId) {
dbData.parent_instance_id = instanceData.parentInstanceId;
}
  if (instanceData.voidedAt) {
dbData.voided_at = instanceData.voidedAt;
}
  if (instanceData.voidedBy) {
dbData.voided_by = instanceData.voidedBy;
}
  if (instanceData.voidReason) {
dbData.void_reason = instanceData.voidReason;
}

  return dbData;
}

/**
 * Convert frontend invitation data to database insert
 */
export function invitationToDatabase(
  invitationData: Partial<InvitationData>,
  recruitId: string,
  organizationId: string,
  token: string,
  userId?: string
): RecruitmentInvitationInsert {
  return {
    organization_id: organizationId,
    recruit_id: recruitId,
    token,
    expires_at: invitationData.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: invitationData.status || 'sent',
    message: invitationData.message || null,
    sent_by: userId || null,
    cc_emails: invitationData.ccEmails || null
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Type guard: Check if value is a valid RecruitStatus
 */
export function isRecruitStatus(value: unknown): value is RecruitStatus {
  return typeof value === 'string' &&
    ['Inquiry', 'Pending', 'In Progress', 'Approved', 'Denied', 'On Hold', 'Terminated'].includes(value);
}

/**
 * Type guard: Check if value is a valid RecruitPosition
 */
export function isRecruitPosition(value: unknown): value is RecruitPosition {
  return typeof value === 'string' &&
    ['Loan Officer', 'Senior Loan Officer', 'Branch Manager'].includes(value);
}

/**
 * Type guard: Check if value is a valid ContractStatus
 */
export function isContractStatus(value: unknown): value is ContractStatus {
  return typeof value === 'string' &&
    ['draft', 'sent', 'viewed', 'accepted', 'recruit_signed', 'signed', 'voided'].includes(value);
}

/**
 * Type guard: Check if value is a valid InvitationStatus
 */
export function isInvitationStatus(value: unknown): value is InvitationStatus {
  return typeof value === 'string' &&
    ['sent', 'viewed', 'started', 'completed', 'expired', 'revoked'].includes(value);
}

/**
 * Batch convert array of database recruits to frontend type
 */
export function recruitsFromDatabase(dbRecruits: Recruit[]): RecruitmentCandidate[] {
  return dbRecruits.map(recruitFromDatabase);
}

/**
 * Batch convert array of database contract instances to frontend type
 */
export function contractInstancesFromDatabase(
  dbInstances: ContractInstance[]
): ContractInstanceData[] {
  return dbInstances.map(contractInstanceFromDatabase);
}

/**
 * Batch convert array of database invitations to frontend type
 */
export function invitationsFromDatabase(
  dbInvitations: RecruitmentInvitation[]
): InvitationData[] {
  return dbInvitations.map(invitationFromDatabase);
}

// ============================================================================
// Re-exports for Convenience
// ============================================================================

export type {
  // Database types (for service layer)
  Recruit,
  RecruitInsert,
  RecruitUpdate,
  RecruitWithRelations,
  ContractTemplate,
  ContractTemplateInsert,
  ContractTemplateUpdate,
  ContractInstance,
  ContractInstanceInsert,
  ContractInstanceUpdate,
  RecruitmentInvitation,
  RecruitmentInvitationInsert,
  RecruitmentInvitationUpdate,
  RecruitStatusHistory
};
